import React, { memo, useCallback } from 'react';
import type { Row } from 'react-table';

import type { EntityModel } from '../../../pages/Trafficking/TraffickingPage/modelConverters';
import bem from '../../../utils/bem';
import { TraffickingTableCells } from '../TraffickingTableCells';

export type TraffickingTableRowProps = {
  row: Row<EntityModel>;
  /**
   * Ordered visible column ids for prepared row cells (`id|id|…`). Primitive snapshot for memo: react-table mutates
   * `row.cells` in place, so reading cells off `row` inside the comparator can miss visibility/order changes.
   */
  visibleColumnIdsKey: string;
  /** Pass separately from row: react-table mutates row.isSelected in place, so comparing row.isSelected in memo breaks. */
  isRowSelected: boolean;
  selectedColIndex: number;
  selectedRowIndex: number;
  onTableRowClick: (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, rowId: Row<EntityModel>['id']) => void;
};

const [, element] = bem('trafficking-table');

const TraffickingTableRow = memo(
  ({
    row,
    isRowSelected,
    selectedColIndex,
    selectedRowIndex,
    onTableRowClick,
  }: TraffickingTableRowProps): JSX.Element => {
    const rowCheckedClass = isRowSelected ? 'checked' : '';

    const onClick = useCallback((e) => onTableRowClick(e, row.id), [onTableRowClick, row.id]);

    return (
      <tr {...row.getRowProps()} className={element('row', rowCheckedClass)} onClick={onClick}>
        <TraffickingTableCells
          cells={row.cells}
          selectedColIndex={selectedColIndex}
          selectedRowIndex={selectedRowIndex}
          isRowSelected={isRowSelected}
        />
      </tr>
    );
  },
  (prev, next) => {
    if (prev.onTableRowClick !== next.onTableRowClick) return false;
    if (prev.isRowSelected !== next.isRowSelected) return false;
    if (prev.row.original !== next.row.original) return false;
    if (prev.visibleColumnIdsKey !== next.visibleColumnIdsKey) return false;

    const prevRowIndex = prev.row.index;
    const nextRowIndex = next.row.index;
    if (prevRowIndex !== nextRowIndex) return false;
    const wasKeyboardSelected = prev.selectedRowIndex === prevRowIndex;
    const isKeyboardSelected = next.selectedRowIndex === nextRowIndex;

    if (wasKeyboardSelected !== isKeyboardSelected) return false;
    if (isKeyboardSelected && prev.selectedColIndex !== next.selectedColIndex) return false;

    return true;
  }
);

TraffickingTableRow.displayName = 'TraffickingTableRow';

export default TraffickingTableRow;
